import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Store, X } from "lucide-react";
import MerchantAgreement from "@/components/MerchantAgreement";

export default function MerchantApplyModal({ open, onClose, presetType = "merchant", onDone }) {
  const [type, setType] = useState(presetType);
  const [businessName, setBusinessName] = useState("");
  const [description, setDescription] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!agree) { toast.error("You must read and agree to the Merchant Agreement"); return; }
    setBusy(true);
    try {
      await api.post("/merchant/apply", { type, business_name: businessName, description, phone, agree });
      toast.success("Merchant application submitted — the Bullpen team will review it shortly");
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 overflow-y-auto" data-testid="merchant-apply-modal">
      <div className="bg-pb-surface border border-pb-border max-w-lg w-full p-6 my-8">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <Store className="w-4 h-4 text-pb-red" />
            <span className="font-display font-bold text-lg uppercase">Merchant Sign-Up</span>
          </div>
          <button onClick={onClose} data-testid="merchant-modal-close"><X className="w-4 h-4 text-zinc-500 hover:text-white" /></button>
        </div>
        <p className="text-xs text-zinc-500 mb-4">
          Only approved Merchants and Card Merchants can redeem earned coins for settlement payouts. Fill this out,
          agree to the terms, and the Bullpen team will review your application.
        </p>
        <div className="space-y-3">
          <div className="flex gap-2">
            {[["merchant", "Merchant", "Goods & services"], ["card_merchant", "Card Merchant", "Sell sports cards"]].map(([v, l, d]) => (
              <button key={v} data-testid={`merchant-type-${v}`} onClick={() => setType(v)}
                className={`flex-1 text-left px-3 py-2 border transition-colors ${type === v ? "border-pb-red bg-pb-red/10" : "border-pb-border hover:border-zinc-500"}`}>
                <div className="text-[10px] font-black uppercase">{l}</div>
                <div className="text-[8px] uppercase tracking-widest text-zinc-500">{d}</div>
              </button>
            ))}
          </div>
          <input data-testid="merchant-business-name" value={businessName} onChange={(e) => setBusinessName(e.target.value)}
            placeholder="Business / seller name *"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <input data-testid="merchant-phone" value={phone} onChange={(e) => setPhone(e.target.value)}
            placeholder="Contact phone"
            className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <textarea data-testid="merchant-description" value={description} onChange={(e) => setDescription(e.target.value)}
            placeholder={type === "card_merchant" ? "Tell us about the cards you plan to sell…" : "What goods or services will you offer?"}
            rows={2} className="w-full bg-pb-elevated border border-pb-border px-3 py-2.5 text-sm text-white focus:outline-none focus:border-pb-red" />
          <MerchantAgreement />
          <label className="flex items-start gap-2 cursor-pointer" data-testid="merchant-agree-label">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)}
              className="accent-[#BEF264] w-4 h-4 mt-0.5" data-testid="merchant-agree-checkbox" />
            <span className="text-xs text-zinc-300">
              I have read and agree to the <span className="font-bold text-white">Pickle Bullpen Merchant Agreement</span> above.
            </span>
          </label>
          <button data-testid="merchant-submit-btn" onClick={submit} disabled={busy || !agree || !businessName.trim()}
            className="w-full flex items-center justify-center gap-2 bg-pb-red text-black font-black uppercase tracking-widest py-3 text-xs hover:bg-[#BEF264] active:scale-95 transition-all disabled:opacity-40">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Store className="w-4 h-4" />} Submit Merchant Application
          </button>
        </div>
      </div>
    </div>
  );
}
